/**
 * demo-scripts.ts — the canned script library for the demo Scripts tab.
 *
 * ORIGINAL sample wording only (no branded/proprietary protocol text). Each script
 * is plain data (paragraphs) so the Scripts tab can render, print, and let a
 * prospect add their own — all in memory, reset on reload.
 */

export interface DemoScript {
  id: string;
  name: string;
  category: string;
  /** One paragraph per entry. */
  body: string[];
  /** True for scripts the prospect created in this session. */
  custom?: boolean;
}

export const SCRIPT_CATEGORIES = ["Grounding", "Processing", "Closing"] as const;

export const SEED_SCRIPTS: DemoScript[] = [
  {
    id: "grounding-resourcing",
    name: "Grounding & Resourcing",
    category: "Grounding",
    body: [
      `Before we begin, let's take a moment to settle. There's nothing to fix right now — we're just getting comfortable.`,
      `Find a position that feels steady. Let your shoulders drop. Notice the chair supporting you and your feet on the floor.`,
      `When you're ready, let your eyes follow the movement on the screen. You don't have to concentrate — just let your gaze travel with it, side to side.`,
      `Now bring to mind a place — real or imagined — where you feel calm and safe. A room, a shoreline, a memory. Picture it as clearly as you can.`,
      `What do you notice there? The colors, the sounds, the temperature. Let yourself stay with it for a moment.`,
      `Notice where the calm sits in your body — maybe your chest, your hands, your breath. Stay with that as you keep following the movement.`,
      `Anytime you need to, you can return to this place. We'll come back to it before we finish.`,
    ],
  },
  {
    id: "target-processing",
    name: "Target Processing Sequence",
    category: "Processing",
    body: [
      `Bring to mind the topic we agreed to work on today. You don't need to relive it — just let it be present, the way it is right now.`,
      `As you hold it in mind, notice where you feel it in your body. There's no right answer — wherever your attention lands is fine.`,
      `Let your eyes follow the movement, and just notice whatever comes up — thoughts, images, feelings, sensations. Let them come and go. Nothing to change.`,
      `Pause the movement. "Take a breath. What are you noticing now?" (Let the client respond, then resume.)`,
      `Go with that. Keep following the movement and let it keep moving on its own.`,
      `If a strong feeling stays put, you can give it a shape — a color, an object, a weight. See what your mind offers.`,
      `Check in: "On a scale of 0 to 10, how much charge does it carry now, compared with when we started?"`,
      `When you're ready, let your attention come back to the room — your feet, the chair, your breath. We'll pause here.`,
    ],
  },
  {
    id: "soft-close",
    name: "Soft Close",
    category: "Closing",
    body: [
      `We're going to slow down now. Let the movement come to a stop and just rest your eyes wherever feels comfortable.`,
      `Take a slower breath. Notice the room around you — the light, the sounds, the ground under your feet.`,
      `If we opened a calm or safe place earlier, let's return to it now for a moment. Picture it again.`,
      `Before we finish: anything you'd like to note for next time — a word, an image, a reminder?`,
      `Take whatever steadiness you found here with you. You can come back to it on your own between sessions.`,
    ],
  },
];
